import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from './UserContext';

const BannerSection = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const UserData = useUser();

  // Inject smooth marquee animation
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
      @keyframes smoothMarquee {
        0% { transform: translateX(100%); }
        100% { transform: translateX(-100%); }
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Preload next banner image
  useEffect(() => {
    if (banners.length > 0) {
      const nextIndex = currentIndex === banners.length - 1 ? 0 : currentIndex + 1;
      const nextBanner = banners[nextIndex];
      if (nextBanner?.banner?.full_size) {
        const img = new Image();
        img.src = nextBanner.banner.full_size;
      }
    }
  }, [currentIndex, banners]);

  // Fetch banners
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    const fetchBanners = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/banners/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
          }
        );
        const data = await response.json();
        const validBanners = data.filter(
          (b) => b?.banner?.full_size || b?.text || b?.message
        );
        setBanners(validBanners);
      } catch (error) {
        console.error("Error fetching banners:", error);
      }
    };
    fetchBanners();
  }, [token, navigate]);

  // Rotate image banners
  useEffect(() => {
    if (!banners.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [banners]);

  // Helper for backend API tracking
  const trackBannerEvent = async (bannerId, eventType) => {
    if (!bannerId) return;
    try {
      const facilityObj = UserData.user?.facility;
      const payload = {
        banner_id: bannerId,
        event_type: eventType,
        facility_id: facilityObj?.id || null,
        facility_name: facilityObj?.name || ""
      };
      console.log("Tracking Banner Event payload:", payload);

      const url = `${process.env.REACT_APP_API_BASE_URL}/track-banner/`;
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error(`Error tracking banner ${eventType}:`, error);
    }
  };

  // Track banner views
  useEffect(() => {
    if (!banners.length) return;

    const currentBanner = banners[currentIndex];

    // API Tracking
    if (currentBanner?.id) {
      trackBannerEvent(currentBanner.id, "view");
    }

    if (!window.gtag) return;
    const rawBannerName = currentBanner?.text || currentBanner?.id || "Unknown";
    const bannerName = rawBannerName.replace(/\s+/g, "_").toLowerCase();

    try {
      window.gtag("event", `banner_${bannerName}_view`, {
        banner_name: rawBannerName,
        banner_id: currentBanner?.id || "unknown",
        banner_position: currentIndex,
      });
    } catch (err) {
      console.error("GTAG banner view event failed:", err);
    }
  }, [currentIndex, banners]);

  const handleBannerClick = (banner) => {
    // API Tracking
    if (banner?.id) {
      trackBannerEvent(banner.id, "click");
    }

    if (!window.gtag) return;

    const rawBannerName = banner?.text || banner?.id || "Unknown";
    const bannerName = rawBannerName.replace(/\s+/g, "_").toLowerCase(); // sanitize name

    console.log("Banner clicked, sending GA event:", banner);

    window.gtag("event", `banner_${bannerName}_click`, {
      banner_name: rawBannerName,
      banner_id: banner?.id || "unknown",
      banner_url: banner?.url || "#",
      banner_position: currentIndex,
    });
  };

  // Handle message click with proper tracking
  const handleMessageClick = (message) => {
    // API Tracking
    if (message?.id) {
      trackBannerEvent(message.id, "click");
    }

    if (!window.gtag) return;

    window.gtag("event", "banner_message_click", {
      message_name: `Message: ${message.message || "Unknown"}`,
      message_id: message.id || "unknown",
      message_url: message.url || "#",
    });

    // Open the URL if available
    if (message.url) {
      window.open(message.url, "_blank", "noopener,noreferrer");
    }
  };

  // Collect all messages from banners
  const messages = banners
    .filter((b) => b?.message)
    .map((b) => ({ id: b.id, message: b.message, url: b.url }));

  if (!banners.length) return null;
  const currentBanner = banners[currentIndex];
  const isImageBanner = currentBanner?.banner?.full_size ? true : false;

  return (
    <div>
      {/* Marquee section for all messages */}
      {messages.length > 0 && (
        <div
          style={{
            overflow: "hidden",
            background: "#f8f9fa",
            margin: "10px 0",
            border: "1px solid #e9ecef",
            borderRadius: "4px",
            position: "relative",
            height: "40px",
          }}
        >
          <div
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
              willChange: "transform",
              animation: "smoothMarquee 20s linear infinite",
              fontSize: "24px",
              color: "red",
              fontWeight: "bold",
            }}
          >
            {messages.map((message, index) => (
              <span
                key={index}
                style={{
                  marginRight: "50px",
                  cursor: message.url ? "pointer" : "default"
                }}
                onClick={() => message.url ? handleMessageClick(message) : null}
              >
                {message.message}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Rotating image banner below marquee */}
      {isImageBanner && (
        <a
          href={currentBanner?.url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault(); // Prevent default to ensure tracking happens before navigation
            handleBannerClick(currentBanner);
            // Navigate after a small delay to ensure tracking completes
            if (currentBanner?.url) {
              setTimeout(() => {
                window.open(currentBanner.url, "_blank", "noopener,noreferrer");
              }, 100);
            }
          }}
        >
          <img
            src={currentBanner.banner.full_size}
            alt={currentBanner?.text || currentBanner?.message || "Banner"}
            style={{ width: "75%", height: "auto" }}
          // style={{ width: "75%", height: "auto", margin: "0 auto" }}
          />
        </a>
      )}
    </div>
  );
};

export default BannerSection;